import { useEffect, useState } from "react";
import classnames from "classnames";
import { useHistory, useParams, withRouter } from "react-router-dom";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";

import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
} from "reactstrap";

import { useFormik } from "formik";
import * as Yup from "yup";
import { ToastContainer } from "react-toastify";

import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import Loader from "../../../../Components/Common/Loader";
import ProfileHeaderCard from "../../../../Components/Common/ProfileHeaderCard";
import { useUpdateUser, useUser } from "../../../../hooks/useUser";
import ChangePasswordForm from "./ChangePasswordForm";
import { getStoredAuthUserId } from "../../../../utils/authUser";

const SettingsSimple = (props) => {
  const { t } = props;
  const history = useHistory();
  const { id: routeId } = useParams();

  // Tabs: personal details, and password. There was previously no way at all
  // to change one's own password from the back-office.
  const [activeTab, setActiveTab] = useState("details");

  const userId = routeId || getStoredAuthUserId();
  const userQ = useUser(userId);
  const updateMutation = useUpdateUser();

  useEffect(() => {
    document.title = t("Modification du profil | Le Conseiller du BTP");
  }, [t]);

  const user = userQ.data || {};
  const profile = user.profile || {};
  const displayName =
    `${profile.first_name || ""} ${profile.last_name || ""}`.trim() ||
    user.username;

  const getErrorMessage = () => {
    const err = updateMutation.error;
    if (!err) return "";
    if (err.data && typeof err.data === "object") {
      const [firstKey] = Object.keys(err.data);
      const firstValue = err.data[firstKey];
      if (Array.isArray(firstValue) && firstValue[0]) return String(firstValue[0]);
      if (typeof firstValue === "string" && firstValue.trim()) return firstValue;
    }
    return err.message || t("Une erreur est survenue");
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      username: user.username || "",
      email: user.email || "",
      phone_number: user.phone_number || "",
      first_name: profile.first_name || "",
      last_name: profile.last_name || "",
      address: profile.address || "",
    },
    validationSchema: Yup.object({
      username: Yup.string().required(t("Le champ est requis")),
      email: Yup.string().email(t("Email invalide")),
    }),
    onSubmit: async (values) => {
      const payload = {
        username: values.username,
        phone_number: values.phone_number,
        profile: {
          first_name: values.first_name,
          last_name: values.last_name,
          address: values.address,
        },
      };

      try {
        await updateMutation.mutateAsync({ id: userId, payload });
        history.push("/pages-profile");
      } catch (_) {
        // handled by toast
      }
    },
  });

  const error = userQ.error || updateMutation.error;
  const errorMessage =
    typeof error === "string" ? error : error?.message || "Erreur";

  if (!userId) return <Loader error={t("Utilisateur non connecté")} />;
  if (userQ.isLoading) return <Loader />;
  if (userQ.isError) return <Loader error={errorMessage} />;

  return (
    <div className="page-content">
      <Container fluid>
        <ToastContainer closeButton={false} />
        <BreadCrumb
          title={t("Modification du profil")}
          pageTitle={t("Profil")}
        />

        <ProfileHeaderCard
          t={t}
          picture={profile.profile_pic}
          name={displayName}
          subtitle={user.email || ""}
          phone={user.phone_number || ""}
          badge={profile.user_type || ""}
        />

        {updateMutation.isError ? (
          <Alert color="danger" className="mb-3">
            {getErrorMessage()}
          </Alert>
        ) : null}

        <Nav tabs className="nav-tabs-custom nav-warning mb-3">
          <NavItem>
            <NavLink
              style={{ cursor: "pointer" }}
              className={classnames({ active: activeTab === "details" })}
              onClick={() => setActiveTab("details")}
            >
              <i className="ri-user-line align-bottom me-1"></i>
              {t("Informations personnelles")}
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              style={{ cursor: "pointer" }}
              className={classnames({ active: activeTab === "password" })}
              onClick={() => setActiveTab("password")}
            >
              <i className="ri-lock-line align-bottom me-1"></i>
              {t("Mot de passe")}
            </NavLink>
          </NavItem>
        </Nav>

        <TabContent activeTab={activeTab}>
        <TabPane tabId="details">
        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <Form
                  onSubmit={(e) => {
                    e.preventDefault();
                    validation.handleSubmit();
                  }}
                >
                  <Row>
                    <Col lg={6}>
                      <div className="mb-3">
                        <Label className="form-label" htmlFor="username">
                          {t("Identifiant")}
                        </Label>
                        <Input
                          id="username"
                          name="username"
                          type="text"
                          value={validation.values.username}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          invalid={
                            validation.touched.username &&
                            validation.errors.username
                              ? true
                              : false
                          }
                        />
                        {validation.touched.username &&
                        validation.errors.username ? (
                          <FormFeedback type="invalid">
                            {validation.errors.username}
                          </FormFeedback>
                        ) : null}
                      </div>
                    </Col>

                    <Col lg={6}>
                      <div className="mb-3">
                        <Label className="form-label" htmlFor="email">
                          {t("Email")}
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={validation.values.email}
                          readOnly
                          disabled
                          invalid={
                            validation.touched.email && validation.errors.email
                              ? true
                              : false
                          }
                        />
                        <small className="text-muted d-block mt-1">
                          {t("L'email de connexion ne peut pas etre modifie ici.")}
                        </small>
                      </div>
                    </Col>

                    <Col lg={6}>
                      <div className="mb-3">
                        <Label className="form-label" htmlFor="phone_number">
                          {t("Téléphone")}
                        </Label>
                        <Input
                          id="phone_number"
                          name="phone_number"
                          type="text"
                          value={validation.values.phone_number}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                        />
                      </div>
                    </Col>

                    <Col lg={6}>
                      <div className="mb-3">
                        <Label className="form-label" htmlFor="first_name">
                          {t("Prénom")}
                        </Label>
                        <Input
                          id="first_name"
                          name="first_name"
                          type="text"
                          value={validation.values.first_name}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                        />
                      </div>
                    </Col>

                    <Col lg={6}>
                      <div className="mb-3">
                        <Label className="form-label" htmlFor="last_name">
                          {t("Nom")}
                        </Label>
                        <Input
                          id="last_name"
                          name="last_name"
                          type="text"
                          value={validation.values.last_name}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                        />
                      </div>
                    </Col>

                    <Col lg={12}>
                      <div className="mb-3">
                        <Label className="form-label" htmlFor="address">
                          {t("Adresse")}
                        </Label>
                        <Input
                          id="address"
                          name="address"
                          type="text"
                          value={validation.values.address}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                        />
                      </div>
                    </Col>

                    <Col lg={12}>
                      <div className="text-end">
                        <Button
                          type="button"
                          color="soft-primary"
                          onClick={() => history.push("/pages-profile")}
                        >
                          {t("Quitter")}
                        </Button>
                        <Button
                          type="submit"
                          color="warning"
                          className="ms-2"
                          disabled={updateMutation.isPending}
                        >
                          {updateMutation.isPending
                            ? t("Enregistrement...")
                            : t("Enregistrer")}
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
        </TabPane>

        <TabPane tabId="password">
          <Row>
            <Col lg={12}>
              <ChangePasswordForm t={t} />
            </Col>
          </Row>
        </TabPane>
        </TabContent>
      </Container>
    </div>
  );
};

SettingsSimple.propTypes = {
  location: PropTypes.object,
  t: PropTypes.any,
};

export default withRouter(withTranslation()(SettingsSimple));
